import { useState, useCallback, useMemo } from 'react';
import { PostCard } from '../components/feed/PostCard';
import { PostComposer } from '../components/feed/PostComposer';
import { FeedFilters } from '../components/feed/FeedFilters';
import { PostSkeleton } from '../components/common/Skeleton';
import { SponsoredAd, MOCK_ADS } from '../components/feed/SponsoredAd';
import { VideoPost, MOCK_VIDEOS } from '../components/feed/VideoPost';
import { ShortsRow } from '../components/feed/ShortsRow';
import { useFeed } from '../hooks/useFeed';
import { loadState, saveState, emitNotification } from '../lib/storage';
import type { SandboxProfile, PostType, AuthorType, ReactionType, FeedFilter } from '../types/sandbox';

interface FeedPageProps {
  profile: SandboxProfile | null;
}

export function FeedPage({ profile }: FeedPageProps) {
  const [filter, setFilter] = useState<FeedFilter>({});
  const { posts, loading, addPost, react } = useFeed(filter);
  const [credits, setCredits] = useState(() => loadState('credits', 5000));

  const handleTypeChange = useCallback((postType?: PostType) => {
    setFilter(prev => ({ ...prev, postType }));
  }, []);

  const handleAuthorChange = useCallback((authorType?: AuthorType) => {
    setFilter(prev => ({ ...prev, authorType }));
  }, []);

  const handlePost = useCallback(async (content: string, groupSlug?: string) => {
    if (!profile) return;
    await addPost({
      author_type: 'operator',
      author_did: profile.operator_did,
      post_type: 'text',
      content,
      group_slug: groupSlug,
    });
  }, [profile, addPost]);

  const handleReact = useCallback((postId: string, reaction: ReactionType) => {
    react(postId, reaction);
  }, [react]);

  const handleBoost = useCallback((postId: string, cost: number) => {
    setCredits((prev: number) => {
      const next = Math.max(0, prev - cost);
      saveState('credits', next);
      return next;
    });
    emitNotification({
      type: 'system',
      emoji: '🚀',
      title: 'Post Boosted!',
      description: `You spent ${cost} credits to boost a post.`,
      time: 'Just now',
    });
  }, []);

  // Build the interleaved feed: posts + ads + videos + shorts
  const feedItems = useMemo(() => {
    if (posts.length === 0) return [];

    const items: { type: 'post' | 'ad' | 'video' | 'shorts'; key: string; index: number }[] = [];
    let adIdx = 0;
    let vidIdx = 0;

    posts.forEach((_, i) => {
      items.push({ type: 'post', key: `post-${i}`, index: i });

      // After 1st post: shorts row
      if (i === 0) {
        items.push({ type: 'shorts', key: 'shorts-row', index: 0 });
      }
      // After 2nd post: first ad
      if (i === 1 && MOCK_ADS[adIdx]) {
        items.push({ type: 'ad', key: `ad-${adIdx}`, index: adIdx });
        adIdx++;
      }
      // After 3rd post: first video
      if (i === 2 && MOCK_VIDEOS[vidIdx]) {
        items.push({ type: 'video', key: `vid-${vidIdx}`, index: vidIdx });
        vidIdx++;
      }
      // After 4th post: second ad
      if (i === 3 && MOCK_ADS[adIdx]) {
        items.push({ type: 'ad', key: `ad-${adIdx}`, index: adIdx });
        adIdx++;
      }
      // After 5th post: second video
      if (i === 4 && MOCK_VIDEOS[vidIdx]) {
        items.push({ type: 'video', key: `vid-${vidIdx}`, index: vidIdx });
        vidIdx++;
      }
    });

    return items;
  }, [posts]);

  return (
    <div className="space-y-3">
      {/* Composer */}
      {profile && (
        <PostComposer profile={profile} onPost={handlePost} />
      )}

      {/* Filters */}
      <FeedFilters
        activeType={filter.postType}
        activeAuthor={filter.authorType}
        onTypeChange={handleTypeChange}
        onAuthorChange={handleAuthorChange}
      />

      {/* Feed */}
      {loading ? (
        <div className="space-y-2">
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
        </div>
      ) : posts.length === 0 ? (
        <div className="empty-state social-box">
          <div className="empty-icon">📭</div>
          <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>No Signal Detected</div>
          <p className="text-2xs mt-1" style={{ color: 'var(--text-muted)' }}>
            The feed is empty. Spawn an agent or write a post to break the silence.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {feedItems.map(item => {
            switch (item.type) {
              case 'post':
                return <PostCard key={item.key} post={posts[item.index]} onReact={handleReact} credits={credits} onBoost={handleBoost} />;
              case 'ad':
                return <SponsoredAd key={item.key} ad={MOCK_ADS[item.index]} />;
              case 'video':
                return <VideoPost key={item.key} video={MOCK_VIDEOS[item.index]} />;
              case 'shorts':
                return <ShortsRow key={item.key} />;
              default:
                return null;
            }
          })}
        </div>
      )}
    </div>
  );
}
